<template>
  <div class="organization-positions-table-wrapper">
    <PositionsHeaderDashboard
      v-if="ideaMdp557Enabled && indicatorCounts"
      class="positions-header-dashboard--grid mb-4"
      :active-count="indicatorCounts.activeCount"
      :draft-count="indicatorCounts.draftCount"
      :invalid-count="indicatorCounts.invalidCount"
      :vacant-count="indicatorCounts.vacantCount"
      :active-indicator="activeIndicatorKey"
      :show-invalid-card="showPositionInvalidIndicator"
      :load-failed="loadFailed"
      :action-label="activeIndicatorKey ? 'Clear Filter' : 'Apply Filter'"
      @select-indicator="handleIndicatorSelect"
    />
    <UniversalTable
      ref="universalTableRef"
      :config="tableConfigWithHideSearch"
      :items="mappedPositions"
      :items-count="backendPagination ? pagination.total : totalFilteredCount"
      :backend-pagination="true"
      :externalCurrentPage="
        backendPagination ? pagination.page : viewState.state.pagination.page
      "
      :loading="isTableLoading"
      :load-failed="showLoadError"
      :emptyObjectData="{
        singular: 'Position',
        plural: 'Positions',
        emptyTitle: 'No positions created',
        emptyMessage:
          'In an Organization you need Positions to be filled by Employees',
        emptyButtonLabel: 'Add your first position',
        emptyContentMarginTop: '100px',
      }"
      @onActionClick="handleAddPosition"
      @onSearch="handleSearch"
      @onClick="handleFilterClick"
      @removeFilter="handleRemoveFilter"
      @removeAllFilters="handleClearFilters"
      @onRequest="handlePageRequest"
      @onSort="handleSort"
      @updatePositionsViewSelection="handleUpdatePositionsViewSelection"
      @onMultipleFieldSearch="handleMultipleFieldSearch"
      :department-filter="ideaMdp440Enabled ? viewState.state.filters.department : null"
      :department-scope="viewState.state.filters.departmentScope"
      @toggleDepartmentScope="handleGridToggleDepartmentScope"
      @clearDepartmentFilter="handleGridClearDepartmentFilter"
    >
      <template #load-error>
        <div
          class="positions-load-error mt-5"
          role="alert"
          data-testid="positions-load-error"
        >
          <EmptyState
            icon="alert-circle"
            title="Couldn't load positions"
            supportingText="Check your connection and try again."
          />
          <Button
            class="positions-load-error__retry"
            outline="outline-default"
            label="Retry"
            :backgroundColor="'#ffffff'"
            size="medium"
            @onClick="handleRetryLoad"
          />
        </div>
      </template>
      <template #Dropdown-activator>
        <div class="icon-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </UniversalTable>

    <!-- MDP-615 — Categorized view: assign/unassign a classification from a row.
         Dialogs live here (not inside the tag cells) so the table stays presentational. -->
    <template v-if="categorizedViewVisible">
      <DepartmentPickerDialog
        :value="viewState.state.categorization.departmentPicker.show"
        :departmentTree="viewState.state.categorization.departmentPicker.tree"
        :loading="viewState.state.categorization.departmentPicker.assigning"
        :error="viewState.state.categorization.departmentPicker.error"
        @input="viewState.state.categorization.departmentPicker.show = $event"
        @close="viewState.closeCategorizedDepartmentPicker()"
        @assign="viewState.assignCategorizedDepartment($event)"
      />

      <JobCategoryPickerDialog
        :open="viewState.state.categorization.jobCategoryPicker.show"
        :model-value="viewState.state.categorization.jobCategoryPicker.selection"
        :results="viewState.state.categorization.jobCategoryPicker.results"
        :loading="viewState.state.categorization.jobCategoryPicker.loading"
        :busy="viewState.state.categorization.jobCategoryPicker.assigning"
        :error="viewState.state.categorization.jobCategoryPicker.error"
        @update:open="viewState.state.categorization.jobCategoryPicker.show = $event"
        @update:modelValue="
          viewState.state.categorization.jobCategoryPicker.selection = $event
        "
        @search="viewState.loadCategorizedJobCategories($event)"
        @assign="viewState.assignCategorizedJobCategory($event)"
        @cancel="viewState.closeCategorizedJobCategoryPicker()"
      />

      <JobFamilyPickerDialog
        :open="viewState.state.categorization.jobFamilyPicker.show"
        :model-value="viewState.state.categorization.jobFamilyPicker.selection"
        :results="viewState.state.categorization.jobFamilyPicker.results"
        :loading="viewState.state.categorization.jobFamilyPicker.loading"
        :busy="viewState.state.categorization.jobFamilyPicker.assigning"
        :error="viewState.state.categorization.jobFamilyPicker.error"
        :default-expanded="true"
        :visible-row-count="5"
        @update:open="viewState.state.categorization.jobFamilyPicker.show = $event"
        @update:modelValue="
          viewState.state.categorization.jobFamilyPicker.selection = $event
        "
        @search="viewState.loadCategorizedJobFamilies($event)"
        @assign="viewState.assignCategorizedJobFamily($event)"
        @cancel="viewState.closeCategorizedJobFamilyPicker()"
      />

      <DetachPositionDialog
        v-if="viewState.state.categorization.detachDialog.show"
        v-model="viewState.state.categorization.detachDialog.show"
        :parent-kind="categorizedDetachParentKind"
        action-label="Remove"
        :position_name="categorizedDetachPositionName"
        :parent_name="viewState.state.categorization.detachDialog.parentName"
        @onAction="viewState.confirmCategorizedUnassign()"
      />
    </template>

    <FilterPositionsModal
      v-if="ideaMdp440Enabled"
      :value="viewState.state.filters.showFilterDialog"
      :initial-statuses="gridFilterModalInitialStatuses"
      :initial-include-archived="gridFilterModalInitialIncludeArchived"
      :initial-radio-filters="gridFilterModalInitialRadioFilters"
      :show-radio-groups="ideaMdp551Enabled"
      :show-roles-attached-group="ideaMdp558Enabled"
      :initial-department="viewState.state.filters.department"
      :initial-scope="viewState.state.filters.departmentScope"
      :departments="gridFilterModalDepartments"
      :loading="gridFilterModalDepartmentsLoading"
      @input="viewState.state.filters.showFilterDialog = $event"
      @close="viewState.state.filters.showFilterDialog = false"
      @search="handleGridDepartmentSearch"
      @apply="handleGridFilterModalApply"
    />
    <SearchModal
      v-else
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="handleApplyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Positions"
      :filterGroups="viewState.state.filters.filterGroups"
      :groupsItems="viewState.state.filters.groupsItems"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="620"
      :defaultSelected="viewState.state.filters.values"
    />

    <!-- Add Position Modal -->
    <AddPositionDialog
      :show="viewState.state.addPositionModal.show"
      @onAdd="
        handlePositionOperation(positions.operations.CREATE_POSITION, $event)
      "
      OrgObjectType="Position"
      ghostText="+ Add"
      type="add"
      :button-label="isAppLoading ? 'Save as Draft' : 'Save as Draft'"
      :allowSpecialCharacters="true"
      :maxWidth="'600'"
      @close="viewState.state.addPositionModal.show = false"
    />

    <!-- Activate Position Modal -->
    <ActivatePositionDialog
      v-model="viewState.state.activatePositionModal.show"
      :name="currentPositionName"
      @onAction="
        handlePositionOperation(positions.operations.ACTIVATE_POSITION)
      "
      @onClose="viewState.state.activatePositionModal.show = false"
    />

    <!-- Delete Position Modal -->
    <DeletePositionDialog
      v-model="viewState.state.deletePositionModal.show"
      :name="currentPositionName"
      @onAction="handlePositionOperation(positions.operations.DELETE_POSITION)"
      @onClose="viewState.state.deletePositionModal.show = false"
    />

    <!-- Rename Position Modal -->
    <RenamePositionDialog
      v-model="viewState.state.renamePositionModal.show"
      :name="currentPositionName"
      :errorMessage="viewState.state.renamePositionModal.errorMessage || ''"
      type="rename"
      @onSave="
        handlePositionOperation(positions.operations.RENAME_POSITION, $event)
      "
      @clearError="viewState.state.renamePositionModal.errorMessage = ''"
    />

    <!-- Archive Position Modal -->
    <ArchivePositionDialog
      v-if="viewState.state.archiveAction === 'archive'"
      v-model="viewState.state.archivePositionModal.show"
      :name="currentPositionName"
      :error-message="viewState.state.archivePositionModal.errorMessage || ''"
      @onAction="
        handlePositionOperation(positions.operations.ARCHIVE_POSITION)
      "
      @onClose="
        viewState.state.archivePositionModal.show = false;
        viewState.state.archivePositionModal.errorMessage = '';
      "
    />
    
    <!-- Unarchive Position Modal -->
    <UnarchivePositionDialog
      v-if="viewState.state.archiveAction === 'unarchive'"
      v-model="viewState.state.archivePositionModal.show"
      :name="currentPositionName"
      :error-message="viewState.state.archivePositionModal.errorMessage || ''"
      @onAction="
        handlePositionOperation(positions.operations.ARCHIVE_POSITION)
      "
      @onClose="
        viewState.state.archivePositionModal.show = false;
        viewState.state.archivePositionModal.errorMessage = '';
      "
    />

    <!-- Attach Role Modal -->
    <AttachedRole
      v-model="viewState.state.attachRoleModal.show"
      OrgObjectType="Role to Position"
      @onSave="viewState.attachRole(accountID)"
      :items="viewState.state.attachRoleModal.roles"
      @item-selected="viewState.handleRoleSelected"
      @add-item-selected="viewState.handleAddRoleSelected"
      :parent-name="currentPositionName"
      :existingNamesList="[]"
      :openDefault="viewState.state.openDefault"
      :isLoadingMore="viewState.state.attachRoleModal.isLoadingMore"
      :hasMoreData="viewState.state.attachRoleModal.hasMoreData"
      @load-more="viewState.loadMoreRoles"
      @search="viewState.searchRoles"
      :type="
        showAttachResponsibilitiesPicker
          ? 'attach_role_to_position_new_picker'
          : 'attach_role_to_position'
      "
      :position="addAndAttachPosition"
      @created-and-attached="handleRoleCreatedAndAttached"
    />

    <!-- Relieve Primary Assignment Modal -->
    <RelievePrimaryAssignment
      v-if="viewState.state.relieveAssignment.show"
      :show="viewState.state.relieveAssignment.show"
      :type="viewState.state.relieveAssignment.type"
      :avatar="viewState.getRelieveAssignmentAvatar()"
      :current-position="currentPositionName"
      @onClose="viewState.closeRelieveAssignment()"
      @onAction="viewState.relievePrimaryAssignment(accountID, $event)"
    />

    <!-- Retire Position Modal -->
    <RetirePositionDialog
      v-model="viewState.state.retirePositionModal.show"
      :name="currentPositionName"
      @onAction="handlePositionOperation(positions.operations.RETIRE_POSITION)"
      @onClose="viewState.state.retirePositionModal.show = false"
    />

    <!-- Reactivate Position Modal -->
    <ReactivatePositionDialog
      v-model="viewState.state.reactivatePositionModal.show"
      :name="currentPositionName"
      @onAction="
        handlePositionOperation(positions.operations.REACTIVATE_POSITION)
      "
      @onClose="viewState.state.reactivatePositionModal.show = false"
    />


    <!-- Clone Position Modal -->
    <ClonePositionDialog
      :modelValue="viewState.state.clonePositionModal.show"
      :positionName="currentPositionName"
      @update:modelValue="viewState.state.clonePositionModal.show = $event"
      @onAction="handlePositionOperation(positions.operations.CLONE_POSITION)"
    />

    <!-- Chatter Drawer -->
    <ChecklistDrawer
      ref="drawerContainer"
      :type="chatterModalType"
      :drawer="chatterModalShow"
      :typeValue="chatterModalTypeValue"
      :object-type="chatterModalType"
      :object-id="chatterModalRecordId"
      @closeDrawer="viewState.closePositionChatterModal()"
      :chatterList="chatterModalData"
      :totalCheckpointChatter="chatterModalTotal"
      iconName="org_icon_position"
      @requestMoreCheckpointsChatter="viewState.requestMoreChatter()"
      :isEmployeeChatter="chatterModalIsEmployeeChatter"
      :avatar="chatterModalAvatar"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch, ref, getCurrentInstance } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import EmptyState from "@components/EmptyState/EmptyState.vue";
import Button from "@components/buttons/Button.vue";
import { areDefaultFiltersApplied } from "@components/constants/filterDefaultsRegistry";
import { matchIndicatorKey } from "@components/HeaderDashboardCards/matchIndicatorKey";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import FilterPositionsModal from "@components/FilterPositionsModal/FilterPositionsModal.vue";
import PositionsHeaderDashboard from "@components/PositionsHeaderDashboard/PositionsHeaderDashboard.vue";
import AddPositionDialog from "@components/AddPositionDialog/AddPositionDialog.vue";
import RenamePositionDialog from "@components/PositionDialogs/RenamePositionDialog/RenamePositionDialog.vue";
import AttachedRole from "@components/RolesCatalog/AttachedRole.vue";
import RelievePrimaryAssignment from "@components/organizations/employees/RelievePrimaryAssignment.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import ActivatePositionDialog from "@components/PositionDialogs/ActivatePositionDialog/ActivatePositionDialog.vue";
import DeletePositionDialog from "@components/PositionDialogs/DeletePositionDialog/DeletePositionDialog.vue";
import ArchivePositionDialog from "@components/PositionDialogs/ArchivePositionDialog/ArchivePositionDialog.vue";
import UnarchivePositionDialog from "@components/PositionDialogs/UnarchivePositionDialog/UnarchivePositionDialog.vue";
import RetirePositionDialog from "@components/PositionDialogs/RetirePositionDialog/RetirePositionDialog.vue";
import ReactivatePositionDialog from "@components/PositionDialogs/ReactivatePositionDialog/ReactivatePositionDialog.vue";
import ClonePositionDialog from "@components/ClonePositionDialog/ClonePositionDialog.vue";
import DepartmentPickerDialog from "@components/DepartmentPickerDialog/DepartmentPickerDialog.vue";
import JobCategoryPickerDialog from "@components/JobCategoryPickerDialog/JobCategoryPickerDialog.vue";
import JobFamilyPickerDialog from "@components/JobFamilyPickerDialog/JobFamilyPickerDialog.vue";
import DetachPositionDialog from "@components/DetachPositionDialog/DetachPositionDialog.vue";
import { useTableConfig, setEventCallbacks } from "./useTableConfig";
import {
  useOrganizationPositionsViewState,
  POSITION_LIST_SEARCH_PARAM_KEYS,
} from "./useViewState";
import { useFilters } from "@/composables/filters/useFilters";
import {
  HEADER_DASHBOARD_FLAGS,
  LD_FLAG_IDEA_MDP_440,
  LD_FLAG_IDEA_MDP_551,
  LD_FLAG_IDEA_MDP_558,
} from "@components/constants/launchDarklyFlagKeys";
import { getListOfDepartments } from "@/api";
import { mapApiDepartmentsToPickerList } from "@components/DepartmentPickerField/departmentPickerFieldUtils.js";
import FilterByDepartmentTag from "@components/FilterByDepartmentTag/FilterByDepartmentTag.vue";
import { buildPositionRadioFilterPayload } from "@components/FilterPositionsModal/positionFilterPayload";
import { bindExiqtiveAccessFiltersChanged } from "@components/utils/bindExiqtiveAccessFiltersChanged";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";
import { usePositionStore } from "@/store/pinia/position";

const app = getCurrentInstance();
const { proxy } = app;

const viewState = useOrganizationPositionsViewState();
const positions = viewState.positions;
// ER-12261: read the indicator counts from the current Pinia store on every
// render (same pattern as the Employees table) instead of going through the
// view-state singleton's cached `positions` reference, so the tiles can never
// hold on to a stale store closure after logout/login.
const positionStore = usePositionStore();
const filter = useFilters();

const accountID = ref(JSON.parse(localStorage.getItem("currentAccountID")));
const drawerContainer = ref(null);
const universalTableRef = ref(null);

/** MDP-440: new FilterPositionsModal (status + archived + department) on the Grid list view; off → legacy SearchModal. */
const ideaMdp440Enabled = ref(false);
/** MDP-551 (ER-10494): radio filter groups (Validity / Occupied / Preparing for) in the new modal. */
const ideaMdp551Enabled = ref(false);
/** MDP-557 (ER-12533): filter-aware indicator row (header dashboard), gated on its own flag. */
const ideaMdp557Enabled = ref(false);
/** MDP-558 (ER-10517): Roles-attached filter group. */
const ideaMdp558Enabled = ref(false);
const showPositionInvalidIndicator = computed(() => isProductReadinessEnabled());
const gridFilterModalDepartments = ref([]);
const gridFilterModalDepartmentsLoading = ref(false);

const GRID_STATUS_FILTER_LABELS = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
  inactive: "Inactive",
};

const gridHasFilterValue = (target) =>
  viewState.state.filters.values.some(
    (f) => String(f).toLowerCase() === String(target).toLowerCase()
  );

const gridFilterModalInitialStatuses = computed(() => ({
  draft: gridHasFilterValue("draft"),
  active: gridHasFilterValue("active"),
  retired: gridHasFilterValue("retired"),
  inactive: gridHasFilterValue("inactive"),
}));

const gridFilterModalInitialIncludeArchived = computed(
  () =>
    viewState.state.filters.values.includes("Show") ||
    viewState.state.filters.values.includes("show_archived")
);

// ER-10494 / MDP-551: current radio group selections (Validity / Occupied / Preparing for),
// rebuilt from the persisted label tokens each time the modal opens.
const gridFilterModalInitialRadioFilters = computed(() =>
  buildPositionRadioFilterPayload(viewState.state.filters.values)
);

// ER-10517 / MDP-558: filter-aware indicator counts (Active / Draft / Invalid / Vacant),
// sourced from the v2 position list response. ER-12261: read straight from the
// current Pinia store so the tiles re-render on every store update (the store
// now resets on account switch and zeros the counts at the start of each load).
const indicatorCounts = computed(() => positionStore.getHeaderCounts);

/**
 * ER-10517 / MDP-558 — one filter value per indicator card. Values are the human
 * labels persisted by the grid and mapped to API params by the view state helper.
 * Active/Draft → Status; Invalid → Validity "Is not valid"; Vacant → Occupied
 * "Vacant".
 */
const POSITION_INDICATOR_FILTERS = {
  active: ["Active"],
  draft: ["Draft"],
  invalid: ["Is not valid"],
  vacant: ["Vacant"],
};

// ER-12619: radio "No Filter" and "Do not Show" archived never render as chips.
const POSITION_INDICATOR_NEUTRAL_VALUES = ["No Filter", "Do not Show", "no_archived"];

const matchPositionIndicatorKey = () =>
  matchIndicatorKey(
    viewState.state.filters.values,
    POSITION_INDICATOR_FILTERS,
    POSITION_INDICATOR_NEUTRAL_VALUES
  );

const activeIndicatorKey = computed(() => {
  const { department, searchText, multipleFieldSearchValues } = viewState.state.filters;
  const hasSearch =
    String(searchText || "").trim() !== "" ||
    (multipleFieldSearchValues || []).some((f) => f.value && String(f.value).trim() !== "");
  if (department?.id != null || hasSearch) return null;
  return matchPositionIndicatorKey();
});

const isAppLoading = computed(() => proxy?.$loader?.isAppLoading || false);
const currentPositionName = computed(
  () => positions.getCurrentPosition()?.name || ""
);
// Position context for AddAndAttachRoleDialog ({ id, code, name })
const addAndAttachPosition = computed(() => {
  const current = positions.getCurrentPosition();
  const code = String(
    current?.position?.position_code ?? current?.code_str ?? current?.code ?? ""
  )
    .trim()
    .replace(/^#/, "")
    .split(" ")[0];
  if (!current?.id || !current?.name || !code) return null;
  return { id: current.id, code, name: current.name };
});
// Role was created and attached in one call by AddAndAttachRoleDialog — just refresh.
const handleRoleCreatedAndAttached = async () => {
  viewState.state.attachRoleModal.show = false;
  await positions.loadAllPositions();
};
const chatterModalShow = computed(
  () => viewState.state.openChatterModal?.show || false
);
const chatterModalData = computed(
  () => viewState.state.openChatterModal?.chatterData || []
);
const chatterModalTotal = computed(
  () => viewState.state.openChatterModal?.totalPositionChatter || 0
);
const chatterModalType = computed(
  () => viewState.state.openChatterModal?.type || "position"
);
const chatterModalTypeValue = computed(
  () => viewState.state.openChatterModal?.typeValue || ""
);
const chatterModalRecordId = computed(
  () => viewState.state.openChatterModal?.recordId ?? null
);
const chatterModalIsEmployeeChatter = computed(
  () => viewState.state.openChatterModal?.isEmployeeChatter || false
);
const chatterModalAvatar = computed(
  () => viewState.state.openChatterModal?.avatar || {}
);

const POSITIONS_FILTERS_KEY = "organizationPositions";

const props = defineProps({
  positions: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  initialLoadComplete: {
    type: Boolean,
    default: true,
  },
  appliedFilters: {
    type: Array,
    default: () => [],
  },
  searchText: {
    type: String,
    default: "",
  },
  backendPagination: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Positions Organization",
  },
  actionLabel: {
    type: String,
    default: "Add Position",
  },
  hideTopSection: {
    type: Boolean,
    default: true,
  },
  hideActiveNo: {
    type: Boolean,
    default: false,
  },
  activeRecordsCount: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  "addPosition",
  "positionClick",
  "closeClick",
  "preparingAvatarsClick",
  "preparingCloseClick",
  "preparingEffectiveDateClick",
  "rowChatterClick",
  "menuClick",
  "search",
  "filterClick",
  "removeFilter",
  "pageChange",
  "sort",
  "onPreparePosition",
  "primaryAssign",
]);

const tableConfig = useTableConfig(viewState);

// Set up event callbacks
setEventCallbacks({
  onCloseClick: (item) => {
    // Set current position and show relieve assignment modal
    positions.setCurrentPosition(item);
    viewState.state.relieveAssignment.show = true;
    viewState.state.relieveAssignment.type = "primary";
    viewState.state.relieveAssignment.assignedEmployee =
      item.assigned_primary || item.occupiedBy;
    viewState.state.relieveAssignment.currentPosition = item;
    emit("closeClick", item);
  },
  onPreparingCloseClick: (item) => {
    // Set current position and show relieve preparing assignment modal
    positions.setCurrentPosition(item);
    viewState.state.relieveAssignment.show = true;
    viewState.state.relieveAssignment.type = "prepare";
    const preparingEmployee =
      item.assigned_preparing_for?.[0] ||
      item.preparingFor?.preparingEmployees?.[0];
    viewState.state.relieveAssignment.assignedEmployee = preparingEmployee;
    viewState.state.relieveAssignment.currentPosition = item;
    emit("preparingCloseClick", item);
  },
  onPreparingAvatarsClick: (item) => emit("preparingAvatarsClick", item),
  onPreparingEffectiveDateClick: (event, item) =>
    emit("preparingEffectiveDateClick", event, item),
  onPreparePosition: (item) => emit("onPreparePosition", item),
  onRowChatterClick: (item) => {
    // Open chatter modal
    positions.setCurrentPosition(item);
    viewState.openPositionChatterModal(item);
    emit("rowChatterClick", item);
  },
  onMenuClick: async ({ item, operation }) => {
    // Handle dropdown menu operations
    positions.setCurrentPosition(item);

    switch (operation?.value) {
      case "activate_action":
        viewState.state.activatePositionModal.show = true;
        break;
      case "delete_action":
        viewState.state.deletePositionModal.show = true;
        break;
      case "rename_action":
        viewState.state.renamePositionModal.show = true;
        break;
      case "attach_position_role":
        viewState.state.openDefault = true;
        await viewState.getListOfRoles(item.id, 1, "", false);
        viewState.state.attachRoleModal.show = true;
        break;
      case "archive_action":
        viewState.state.archiveAction = "archive";
        viewState.state.archivePositionModal.show = true;
        break;
      case "unarchive_action":
        viewState.state.archiveAction = "unarchive";
        viewState.state.archivePositionModal.show = true;
        break;
      case "retire_action":
        viewState.state.retirePositionModal.show = true;
        break;
      case "reactivate_action":
        viewState.state.reactivatePositionModal.show = true;
        break;
      case "deactivate_action":
        viewState.state.deactivatePositionModal.show = true;
        break;
      case "cloned_action":
        viewState.state.clonePositionModal.show = true;
        break;
    }

    emit("menuClick", { item, operation });
  },
  onPositionClick: (item) => emit("positionClick", item),
  onPrimaryAssign: (item) => emit("primaryAssign", item),
});

// Update viewState config from props
viewState.config.title = props.title;
viewState.config.actionLabel = props.actionLabel;
viewState.config.hideTopSection = props.hideTopSection;
viewState.config.hideActiveNo = props.hideActiveNo;
viewState.config.activeRecordsCount = props.activeRecordsCount;

// Sync filters from props only when parent passes an explicit non-empty list.
// Default [] from OrganizationPage must not wipe persisted filters (singleton + localStorage).
watch(
  () => props.appliedFilters,
  (newFilters) => {
    if (Array.isArray(newFilters) && newFilters.length > 0) {
      viewState.state.filters.values = [...newFilters];
    }
  },
  { immediate: true }
);

watch(
  () => props.searchText,
  (newSearchText) => {
    if (newSearchText !== undefined) {
      viewState.state.filters.searchText = newSearchText;
    }
  },
  { immediate: true }
);

// Backend handles all filtering, searching, and sorting - just pass positions through
const filteredAndSortedPositions = computed(() => {
  return [...props.positions];
});

const showAttachResponsibilitiesPicker = computed(() => {
  return Boolean(
    proxy?.$store?.getters?.["featureFlags/ideaMDP389"]
  );
});

// Total count for pagination
const totalFilteredCount = computed(() => {
  return filteredAndSortedPositions.value.length;
});

// Paginated positions (10 per page) - slice for current page when doing frontend pagination
const paginatedPositions = computed(() => {
  if (props.backendPagination) {
    // Backend pagination - pass all items as-is
    return filteredAndSortedPositions.value;
  }

  // Frontend pagination - slice items for current page
  const currentPage = viewState.state.pagination.page || 1;
  const itemsPerPage = props.pagination.perPage || 10;
  const start = (currentPage - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredAndSortedPositions.value.slice(start, end);
});

// Map positions data with IDs
const mappedPositions = computed(() => {
  return paginatedPositions.value.map((item, index) => ({
    id: item.id || `position-${index}-${Math.random()}`,
    ...item,
  }));
});

// ER-12354: the untouched list carries the registered defaults (Draft, Active), which
// count as "no filters applied".
function hasNoFiltersApplied(values) {
  return areDefaultFiltersApplied(POSITIONS_FILTERS_KEY, values);
}

const hasNoItems = computed(() => {
  const itemsList = mappedPositions.value;
  return !Array.isArray(itemsList) || itemsList.length === 0;
});

// The first fetch has finished and no list request is in flight, so an empty list is real
// (not "not loaded yet") and the empty-account state may replace the filter bar.
const isListSettled = computed(() => props.initialLoadComplete && !props.loading);

// While an empty list is (re)loading show the skeleton, never the empty-account message.
const isTableLoading = computed(
  () => props.loading || (!props.initialLoadComplete && hasNoItems.value)
);

// ER-12597: the latest list request failed. The store keeps the previous rows, so the error state
// replaces the empty state only when nothing is left to show.
const loadFailed = computed(() => Boolean(positionStore.getLoadFailed));
const showLoadError = computed(
  () => loadFailed.value && isListSettled.value && hasNoItems.value
);

const handleRetryLoad = async () => {
  await positions.loadAllPositions();
};

const hideSearchAndFilters = computed(() => {
  const hasTextSearch =
    String(viewState.state.filters.searchText || "").trim() !== "";
  const hasMultipleFieldSearch = (
    viewState.state.filters.multipleFieldSearchValues || []
  ).some((f) => f.value && String(f.value).trim() !== "");
  const hasNoSearch = !hasTextSearch && !hasMultipleFieldSearch;
  // Chip values (radio "No Filter" entries already dropped), the same list UniversalTable checks.
  const hasNoFilters = hasNoFiltersApplied(tableConfig.tableParams.value.filters.values);
  // An empty result driven by a department filter is not an empty account: keep the bar.
  const hasDepartmentFilter =
    ideaMdp440Enabled.value && viewState.state.filters.department?.id != null;
  return (
    isListSettled.value &&
    hasNoItems.value &&
    !showLoadError.value &&
    hasNoSearch &&
    hasNoFilters &&
    !hasDepartmentFilter
  );
});

const tableConfigWithHideSearch = computed(() => ({
  ...tableConfig.tableParams.value,
  hideSearchAndFilters: hideSearchAndFilters.value,
}));

// Event handlers
const handleAddPosition = () => {
  viewState.state.addPositionModal.show = true;
  emit("addPosition");
};

const handleFilterClick = () => {
  viewState.state.filters.showFilterDialog = true;
  emit("filterClick");
};

const handlePageRequest = async (request) => {
  if (props.backendPagination) {
    positions.setPaginationDataLimit(request.itemsPerPage || 10);
    positions.setPaginationDataOffset(
      (request.page - 1) * (request.itemsPerPage || 10)
    );
    await positions.loadAllPositions();
    emit("pageChange", request.page);
  } else {
    // Frontend pagination - update viewState and UniversalTable will update via externalCurrentPage
    viewState.handleRequest(request);
  }
};

const handleSort = async (sortData) => {
  if (props.backendPagination) {
    const finalSortField = sortData.descending
      ? `-${sortData.field}`
      : `${sortData.field}`;
    positions.setOrderingDataField(finalSortField);
    await positions.loadAllPositions();
    emit("sort", sortData);
  } else {
    // Frontend sorting
    viewState.handleSort(sortData);
  }
};

const handleSearch = async (searchText) => {
  POSITION_LIST_SEARCH_PARAM_KEYS.forEach((key) => {
    positions.removePositionFilter(key);
  });
  if (searchText && searchText.trim() !== "") {
    positions.addPositionFilter("name", searchText);
  }
  positions.setPaginationDataOffset(0);
  viewState.state.filters.searchText = searchText;
  await positions.loadAllPositions();
  emit("search", searchText);
};

const handleApplyFilters = async (filters) => {
  const newFilters = filters
    .map((item) => {
      if (item.value !== null) {
        return typeof item === "string" ? item : item.value;
      }
      return null;
    })
    .filter(Boolean);

  viewState.state.filters.values = newFilters;
  filter.setFilters({
    key: viewState.state.filters.key,
    filters: newFilters,
  });
  viewState.pushFilterValuesToPositionsStore(newFilters);

  viewState.state.filters.showFilterDialog = false;
  positions.setPaginationDataOffset(0);
  await positions.loadAllPositions();
};

const handleGridDepartmentSearch = async (query) => {
  gridFilterModalDepartmentsLoading.value = true;
  try {
    if (!accountID.value) {
      gridFilterModalDepartments.value = [];
      return;
    }
    const { data } = await getListOfDepartments(
      accountID.value,
      query || "",
      { archived: false },
      { limit: 500, offset: 0 }
    );
    const results = Array.isArray(data) ? data : data?.results || [];
    gridFilterModalDepartments.value = mapApiDepartmentsToPickerList(results);
  } catch (error) {
    console.error("Error loading departments for position filter:", error);
    gridFilterModalDepartments.value = [];
  } finally {
    gridFilterModalDepartmentsLoading.value = false;
  }
};

const handleGridFilterModalApply = async (payload) => {
  const newFilters = [];
  (payload?.status || []).forEach((key) => {
    if (GRID_STATUS_FILTER_LABELS[key]) {
      newFilters.push(GRID_STATUS_FILTER_LABELS[key]);
    }
  });
  if (payload?.includeArchived) {
    newFilters.push("Show");
  }
  // ER-10494 / MDP-551: single-select radio groups → human labels the store maps to API params.
  if (payload?.radioFilters) {
    Object.values(payload.radioFilters).forEach((value) => {
      if (value) newFilters.push(value);
    });
  }

  viewState.state.filters.values = newFilters;
  filter.setFilters({
    key: viewState.state.filters.key,
    filters: newFilters,
  });
  viewState.setDepartmentFilter(payload?.department || null, payload?.scope || "subtree", "grid");
  viewState.pushFilterValuesToPositionsStore(newFilters);

  viewState.state.filters.showFilterDialog = false;
  positions.setPaginationDataOffset(0);
  await positions.loadAllPositions();
};

const handleGridToggleDepartmentScope = async () => {
  const newScope = viewState.state.filters.departmentScope === "direct" ? "subtree" : "direct";
  viewState.setDepartmentFilter(viewState.state.filters.department, newScope, "grid");
  viewState.pushFilterValuesToPositionsStore(viewState.state.filters.values);
  positions.setPaginationDataOffset(0);
  await positions.loadAllPositions();
};

const handleGridClearDepartmentFilter = async () => {
  viewState.setDepartmentFilter(null, "subtree", "grid");
  viewState.pushFilterValuesToPositionsStore(viewState.state.filters.values);
  positions.setPaginationDataOffset(0);
  await positions.loadAllPositions();
};

const handleRemoveFilter = async (removedFilter) => {
  viewState.removeFilters(removedFilter);
  await handleApplyFilters(
    viewState.state.filters.values.map((value) =>
      typeof value === "string" ? { label: value, value: value } : value
    )
  );
};

const handleClearFilters = async () => {
  // ER-11195: "Clear all" restores the documented defaults (Draft/Active)
  // from the central registry instead of an unfiltered list.
  viewState.state.filters.values = filter.resetToDefaults(viewState.state.filters.key);
  viewState.state.filters.multipleFieldSearchValues = [];
  viewState.setDepartmentFilter(null, "subtree", "grid");
  viewState.pushFilterValuesToPositionsStore(viewState.state.filters.values);
  positions.setPaginationDataOffset(0);

  // Reset the multiple field search box if it exists
  const multipleFieldSearchRef = universalTableRef.value?.$refs?.searchBarRef?.$refs?.multipleFieldSearchRef;
  if (multipleFieldSearchRef) {
    multipleFieldSearchRef.reset();
  }

  await positions.loadAllPositions();
};

/**
 * ER-10517 / MDP-558 — indicator card click. Each card maps to exactly one filter
 * dimension/value: Active/Draft → Status, Invalid → Validity "Is not valid",
 * Vacant → Occupied "Vacant". Clicking applies only that filter after
 * clearing search, the archived toggle, the department filter and any other
 * filter groups; clicking the active card again returns to the default filter
 * state (Draft/Active).
 */
const handleIndicatorSelect = async (key) => {
  const target = POSITION_INDICATOR_FILTERS[key];
  if (!target) return;

  const isApplied = matchPositionIndicatorKey() === key;

  const nextFilters = isApplied
    ? filter.resetToDefaults(viewState.state.filters.key)
    : [...target];

  // Clear search (simple + multi-field) and the department filter.
  viewState.state.filters.searchText = "";
  viewState.state.filters.multipleFieldSearchValues = [];
  POSITION_LIST_SEARCH_PARAM_KEYS.forEach((paramKey) => {
    positions.removePositionFilter(paramKey);
  });
  viewState.setDepartmentFilter(null, "subtree", "grid");

  viewState.state.filters.values = nextFilters;
  filter.setFilters({
    key: viewState.state.filters.key,
    filters: nextFilters,
  });
  viewState.pushFilterValuesToPositionsStore(nextFilters);
  positions.setPaginationDataOffset(0);

  // Reset the multiple field search box if it exists.
  const multipleFieldSearchRef =
    universalTableRef.value?.$refs?.searchBarRef?.$refs?.multipleFieldSearchRef;
  if (multipleFieldSearchRef) {
    multipleFieldSearchRef.reset();
  }

  await positions.loadAllPositions();
};

const handleUpdatePositionsViewSelection = (selection) => {
  viewState.setPositionsViewSelection(selection);
};

/** MDP-615 — Categorized view + its row-level dialogs. */
const categorizedViewVisible = computed(
  () =>
    viewState.config.positionsViewSelection === "Categorized" &&
    typeof viewState.hasCategorizedView === "function" &&
    viewState.hasCategorizedView()
);

const categorizedDetachParentKind = computed(
  () => viewState.state.categorization.detachDialog.kind || "department"
);

const categorizedDetachPositionName = computed(() => {
  const position = viewState.state.categorization.detachDialog.position;
  if (!position) return "";
  const code = String(position.code_str || "").trim();
  const name = String(position.name || position.position_name || "").trim();
  if (code && name) return `${code} - ${name}`;
  return name || code;
});

// Rule 1/10: if the Categorized segment is hidden while selected, fall back to Grid.
watch(
  () => [
    viewState.state.categorization.ideaMdp615Enabled,
    viewState.state.categorization.departmentFlagEnabled,
    viewState.state.categorization.jobCategoryFlagEnabled,
    viewState.state.categorization.jobCategoriesOn,
    viewState.state.categorization.jobFamilyFlagEnabled,
    viewState.state.categorization.jobFamiliesOn,
  ],
  () => {
    if (
      viewState.config.positionsViewSelection === "Categorized" &&
      typeof viewState.hasCategorizedView === "function" &&
      !viewState.hasCategorizedView()
    ) {
      viewState.config.positionsViewSelection = "Grid";
    }
  }
);

// Position operation handler - same as PositionView.vue
const handlePositionOperation = async (operation, payload = null) => {
  await viewState.handlePositionOperation(operation, accountID.value, payload);
};

const handleMultipleFieldSearch = async (event) => {
  await viewState.handleMultipleFieldSearch(event);
};

onMounted(async () => {
  try {
    ideaMdp440Enabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_440, false)) === true;
  } catch {
    ideaMdp440Enabled.value = false;
  }

  try {
    ideaMdp551Enabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_551, false)) === true;
  } catch {
    ideaMdp551Enabled.value = false;
  }

  try {
    ideaMdp557Enabled.value =
      (await proxy?.$getFlag?.(HEADER_DASHBOARD_FLAGS.positions.launchDarklyKey, false)) === true;
  } catch {
    ideaMdp557Enabled.value = false;
  }

  try {
    ideaMdp558Enabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_558, false)) === true;
  } catch {
    ideaMdp558Enabled.value = false;
  }

  await tableConfig.initializeDropdownFlags();

  // Skip loading persisted filters in Storybook - use props instead
  const isStorybook =
    typeof window !== "undefined" &&
    (window.location?.href?.includes("localhost:6006") ||
      window.location?.href?.includes("storybook"));

  if (!isStorybook) {
    viewState.getFilters();
  }
});

let unbindAccessFiltersChanged = () => {};
onMounted(() => {
  unbindAccessFiltersChanged = bindExiqtiveAccessFiltersChanged(async () => {
    viewState.getFilters();
    await positions.loadAllPositions?.();
  });
});

onUnmounted(() => {
  unbindAccessFiltersChanged();
  viewState.state.filters.searchText = "";
  POSITION_LIST_SEARCH_PARAM_KEYS.forEach((key) => {
    positions.removePositionFilter(key);
  });
});
</script>

<style scoped>
.positions-load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 20px;
  border: 1px solid #d92d20;
  border-radius: 12px;
  background-color: #ffffff;
  padding-bottom: 24px;
}

.positions-load-error .empty-state {
  padding-bottom: 16px;
}

.grid-department-filter-tag {
  margin: 0 16px 8px;
}

.organization-positions-table-wrapper .container {
  padding: 0px !important;
}

::deep(.spmfb__field) {
  padding: 6px 12px !important;
  border: 2px solid red;
}

::deep(.spmfb__chip) {
  height: 24px !important;
}
</style>
